import React, { useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { useWorkflow } from '../context/WorkflowContext';
import { AgentIcon, ExecuteIcon, ToolsIcon } from './Icons';
import CustomDropdown from './CustomDropdown';

function AgentNode({ id, data, selected }) {
  const { providers, updateNodeConfig } = useWorkflow();
  const config = data.config || {};
  const model = config.model || {};
  const isEntryPoint = data.isEntryPoint || false;
  const isActive = data.isActive || false;
  const activeTool = data.activeTool;
  const tools = config.tools || [];
  
  // Custom color from data (default to teal)
  const nodeColor = data.color || '#60D2CC';
  
  // Helper functions for color manipulation
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      };
    }
    return { r: 96, g: 210, b: 204 }; // Default teal
  };
  
  const hexToRgba = (hex, alpha) => {
    const rgb = hexToRgb(hex);
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
  };
  
  // Generate a lighter shade for secondary color (icon fill, etc.)
  const getLighterShade = (hex, factor = 0.3) => {
    const rgb = hexToRgb(hex);
    const lighter = {
      r: Math.min(255, Math.round(rgb.r + (255 - rgb.r) * factor)),
      g: Math.min(255, Math.round(rgb.g + (255 - rgb.g) * factor)),
      b: Math.min(255, Math.round(rgb.b + (255 - rgb.b) * factor))
    };
    return `rgb(${lighter.r}, ${lighter.g}, ${lighter.b})`;
  };
  
  // Generate a darker shade for contrast
  const getDarkerShade = (hex, factor = 0.2) => {
    const rgb = hexToRgb(hex);
    const darker = {
      r: Math.round(rgb.r * (1 - factor)),
      g: Math.round(rgb.g * (1 - factor)),
      b: Math.round(rgb.b * (1 - factor))
    };
    return `rgb(${darker.r}, ${darker.g}, ${darker.b})`;
  };
  
  // Theme detection
  const [isDarkMode, setIsDarkMode] = useState(true);
  
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(theme !== 'light');
    };
    checkTheme();
    
    // Observer for theme changes
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    
    return () => observer.disconnect();
  }, []);
  
  // Generate node accent colors from custom color
  const nodeAccentColor = nodeColor;
  const nodeAccentColorLight = getLighterShade(nodeColor, 0.3);
  const nodeAccentColorDark = getDarkerShade(nodeColor, 0.15);
  
  // Theme-aware colors - using custom nodeColor for accents
  const colors = isDarkMode ? {
    background: isActive 
      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(7, 7, 7, 0.95) 100%)'
      : 'linear-gradient(90deg, #070707 0%, #191919 50%, #070707 100%)',
    border: isActive ? 'rgba(245, 158, 11, 0.5)' : 
            selected ? hexToRgba(nodeColor, 0.5) : 
            isEntryPoint ? 'rgba(16, 185, 129, 0.4)' : 
            '#191919',
    labelColor: '#999',
    nameColor: '#fff',
    textSecondary: '#999',
    dividerColor: '#191919',
    handleBorder: 'var(--bg-primary)',
    handleBg: nodeAccentColor,
    toolBg: hexToRgba(nodeColor, 0.1),
    toolBorder: hexToRgba(nodeColor, 0.3),
    toolColor: nodeAccentColor,
    descColor: '#666',
    iconBg: hexToRgba(nodeColor, 0.2),
    iconBorder: hexToRgba(nodeColor, 0.4),
    iconColor: nodeAccentColor,
    iconColorSecondary: nodeAccentColorLight,
    glowColor: hexToRgba(nodeColor, 0.4),
  } : {
    background: isActive 
      ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(255, 255, 255, 0.95) 100%)'
      : '#ffffff',
    border: isActive ? 'rgba(245, 158, 11, 0.5)' : 
            selected ? hexToRgba(nodeColor, 0.5) : 
            isEntryPoint ? 'rgba(16, 185, 129, 0.4)' : 
            'rgba(25, 25, 25, 0.15)',
    labelColor: '#666',
    nameColor: '#020202',
    textSecondary: '#666',
    dividerColor: 'rgba(25, 25, 25, 0.15)',
    handleBorder: '#ffffff',
    handleBg: nodeAccentColor,
    toolBg: hexToRgba(nodeColor, 0.15),
    toolBorder: hexToRgba(nodeColor, 0.4),
    toolColor: nodeAccentColorDark,
    descColor: '#666',
    iconBg: hexToRgba(nodeColor, 0.15),
    iconBorder: hexToRgba(nodeColor, 0.4),
    iconColor: nodeAccentColorDark,
    iconColorSecondary: nodeAccentColor,
    glowColor: hexToRgba(nodeColor, 0.3),
  };
  
  const currentProvider = model.provider || 'gemini-vertex';
  const currentModel = model.model_id || '';
  
  // Get models for current provider
  const providerData = (providers || []).find(p => p.id === currentProvider);
  const availableModels = providerData?.models || [];
  
  // Truncate model name for display
  const truncateModel = (modelId) => {
    if (!modelId) return 'Select model';
    // Remove common prefixes and truncate
    let display = modelId
      .replace('claude-', '')
      .replace('gpt-', '')
      .replace('gemini-', '')
      .replace('-20250929', '')
      .replace('-20250514', '')
      .replace('-20240620', '');
    return display.length > 18 ? display.slice(0, 16) + '...' : display;
  };
  
  return (
    <div style={{
      padding: '16px',
      border: `1px solid ${colors.border}`,
      borderRadius: '16px',
      background: colors.background,
      minWidth: '200px',
      maxWidth: '280px',
      boxShadow: isActive 
        ? '0 0 30px rgba(245, 158, 11, 0.4), 0 4px 20px rgba(0, 0, 0, 0.3)' 
        : selected 
          ? `0 0 25px ${colors.glowColor}, 0 4px 20px rgba(0, 0, 0, 0.3)` 
          : isDarkMode 
            ? '0 4px 20px rgba(0, 0, 0, 0.4)'
            : '0 4px 20px rgba(0, 0, 0, 0.1)',
      position: 'relative',
      transition: 'all 0.3s ease',
      backdropFilter: 'blur(8px)',
    }}>
      {/* Entry point indicator */}
      {isEntryPoint && (
        <div className="entry-point-badge">
          ▶ ENTRY
        </div>
      )}

      {/* Active indicator */}
      {isActive && (
        <div className="active-indicator">
          <ExecuteIcon size={12} color="#F59E0B" />
        </div>
      )}
      
      {/* Active tool indicator */}
      {activeTool && (
        <div className="active-tool-badge">
          <ToolsIcon size={12} /> {activeTool}
        </div>
      )}
      
      {/* Input handle - always render for swarm return connections
          Entry points still need target handles for sub-agents to hand back */}
      <Handle 
        type="target" 
        position={Position.Top}
        style={{ 
          background: colors.handleBg, 
          width: '14px', 
          height: '14px',
          border: `2px solid ${colors.handleBorder}`,
          // Visually indicate entry point has a target handle too
          opacity: isEntryPoint ? 0.5 : 1,
        }}
      />
      
      {/* Header */}
      <div style={{ marginBottom: '12px' }}>
        <div style={{ 
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '6px'
        }}>
          <div style={{
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: colors.iconBg,
            border: `1px solid ${colors.iconBorder}`,
            borderRadius: '10px',
          }}>
            <AgentIcon size={16} color={colors.iconColor} colorSecondary={colors.iconColorSecondary} />
          </div>
          <div style={{ 
            fontSize: '10px', 
            color: colors.labelColor, 
            textTransform: 'uppercase', 
            letterSpacing: '0.5px',
            fontWeight: '600'
          }}>
            Agent
          </div>
        </div>
        <div style={{ 
          fontWeight: '600', 
          fontSize: '14px',
          color: colors.nameColor,
          lineHeight: '1.3'
        }}>
          {config.name || data.label}
        </div>
      </div>
      
      {/* Info */}
      <div style={{ 
        fontSize: '11px', 
        color: colors.textSecondary,
        borderTop: `1px solid ${colors.dividerColor}`,
        paddingTop: '10px',
      }}>
        {/* Provider & Model selectors */}
        <div 
          style={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            marginBottom: '8px'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Provider dropdown */}
          <CustomDropdown
            value={currentProvider}
            options={providers.map(p => ({ value: p.id, label: p.name }))}
            onChange={(value) => {
              updateNodeConfig(id, 'model.provider', value);
              // Reset model when provider changes
              const newProviderModels = providers.find(p => p.id === value)?.models || [];
              if (newProviderModels.length > 0) {
                updateNodeConfig(id, 'model.model_id', newProviderModels[0]);
              }
            }}
            placeholder="Select provider"
          />
          
          {/* Model dropdown */}
          <CustomDropdown
            value={currentModel}
            options={availableModels.map(m => ({ value: m, label: truncateModel(m) }))}
            onChange={(value) => {
              updateNodeConfig(id, 'model.model_id', value);
            }}
            placeholder="Select model"
          />
        </div>
        
        {/* Tool badges */}
        {tools.length > 0 && (
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '6px',
            marginBottom: '10px'
          }}>
            {tools.slice(0, 3).map((toolId, idx) => (
              <span 
                key={idx} 
                style={{
                  padding: '3px 8px',
                  background: colors.toolBg,
                  border: `1px solid ${colors.toolBorder}`,
                  borderRadius: '6px',
                  fontSize: '9px',
                  color: colors.toolColor,
                  fontFamily: 'JetBrains Mono, monospace',
                  fontWeight: '500',
                }}
              >
                {toolId.split('_').pop()}
              </span>
            ))}
            {tools.length > 3 && (
              <span style={{
                padding: '3px 8px',
                background: hexToRgba(nodeColor, isDarkMode ? 0.2 : 0.25),
                border: `1px solid ${hexToRgba(nodeColor, isDarkMode ? 0.5 : 0.6)}`,
                borderRadius: '6px',
                fontSize: '9px',
                color: colors.toolColor,
                fontFamily: 'JetBrains Mono, monospace',
                fontWeight: '600',
              }}>
                +{tools.length - 3}
              </span>
            )}
          </div>
        )}
        
        {/* Description */}
        {config.description && (
          <div style={{ 
            fontStyle: 'normal',
            color: colors.descColor,
            lineHeight: '1.5',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            fontSize: '11px',
            fontFamily: 'Figtree, sans-serif',
          }}>
            {config.description}
          </div>
        )}
      </div>
      
      {/* Output handle */}
      <Handle 
        type="source" 
        position={Position.Bottom}
        style={{ 
          background: colors.handleBg, 
          width: '14px', 
          height: '14px',
          border: `2px solid ${colors.handleBorder}`,
        }}
      />
    </div>
  );
}

export default AgentNode;
